import { useState, useRef, FormEvent } from 'react';
import { useTheme } from '@/hooks/useTheme';

interface AuditModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const industries = [
  'Law Firm',
  'Accounting Firm',
  'Medical Practice',
  'IT Service Provider',
  'Real Estate Firm',
  'Marketing Agency',
  'Other',
];

export default function AuditModal({ isOpen, onClose }: AuditModalProps) {
  const [status, setStatus] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle');
  const [charCount, setCharCount] = useState(0);
  const formRef = useRef<HTMLFormElement>(null);
  const { theme } = useTheme();
  const isDark = theme === 'dark';

  if (!isOpen) return null;

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (status === 'submitting') return;
    setStatus('submitting');

    const form = e.currentTarget;
    const data = new URLSearchParams();
    const formData = new FormData(form);
    formData.forEach((value, key) => {
      data.append(key, value as string);
    });

    try {
      const res = await fetch('https://readdy.ai/api/form/d769fl54hjod66j2emmg', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: data.toString(),
      });
      if (res.ok) {
        setStatus('success');
        formRef.current?.reset();
        setCharCount(0);
      } else {
        setStatus('error');
      }
    } catch {
      setStatus('error');
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      style={{ background: isDark ? 'rgba(0,0,0,0.75)' : 'rgba(0,0,0,0.5)', backdropFilter: 'blur(4px)' }}
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div className={`relative w-full max-w-lg rounded-2xl border overflow-hidden shadow-2xl transition-colors duration-500 ${
        isDark ? 'border-white/10 bg-[#111827]' : 'border-black/10 bg-white'
      }`}>
        {/* Header */}
        <div className={`flex items-center justify-between px-7 py-5 border-b transition-colors duration-500 ${
          isDark ? 'border-white/6' : 'border-black/6'
        }`}>
          <div>
            <h2 className={`text-lg font-semibold tracking-tight ${isDark ? 'text-white' : 'text-[#1d1d1f]'}`}>Book a System Audit</h2>
            <p className={`text-xs mt-0.5 ${isDark ? 'text-gray-500' : 'text-gray-500'}`}>We&apos;ll analyze your operations and respond within 24 hours.</p>
          </div>
          <button
            onClick={onClose}
            className={`w-8 h-8 flex items-center justify-center rounded-md transition-colors cursor-pointer ${
              isDark ? 'hover:bg-white/5 text-gray-500 hover:text-white' : 'hover:bg-black/5 text-gray-500 hover:text-gray-900'
            }`}
          >
            <i className="ri-close-line text-lg"></i>
          </button>
        </div>

        {/* Body */}
        <div className="px-7 py-6">
          {status === 'success' ? (
            <div className="flex flex-col items-center gap-4 py-8 text-center">
              <div className={`w-14 h-14 flex items-center justify-center rounded-full border ${
                isDark ? 'bg-cyan-400/10 border-cyan-400/20' : 'bg-blue-500/10 border-blue-500/20'
              }`}>
                <i className={`ri-check-line text-2xl ${isDark ? 'text-cyan-400' : 'text-blue-500'}`}></i>
              </div>
              <h3 className={`text-lg font-semibold ${isDark ? 'text-white' : 'text-[#1d1d1f]'}`}>Audit Request Received</h3>
              <p className={`text-sm max-w-xs leading-relaxed ${isDark ? 'text-gray-500' : 'text-gray-500'}`}>
                Thank you. Our team will review your submission and be in touch within 24 hours.
              </p>
              <button
                onClick={onClose}
                className={`mt-2 px-6 py-2.5 rounded-md text-sm font-semibold transition-colors cursor-pointer whitespace-nowrap ${
                  isDark ? 'bg-white text-[#0E1116] hover:bg-gray-100' : 'bg-[#0E1116] text-white hover:bg-gray-800'
                }`}
              >
                Close
              </button>
            </div>
          ) : (
            <form
              ref={formRef}
              onSubmit={handleSubmit}
              data-readdy-form
              className="flex flex-col gap-4"
            >
              {/* Row: Name + Company */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="flex flex-col gap-1.5">
                  <label className={`text-xs font-medium tracking-wide ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>Full Name <span className={isDark ? 'text-cyan-400' : 'text-blue-500'}>*</span></label>
                  <input
                    type="text"
                    name="name"
                    required
                    placeholder="Jane Smith"
                    className={`w-full px-4 py-2.5 rounded-md text-sm placeholder-gray-400 focus:outline-none transition-colors ${
                      isDark
                        ? 'bg-white/5 border border-white/8 text-white focus:border-cyan-400/40'
                        : 'bg-gray-50 border border-gray-200 text-[#1d1d1f] focus:border-blue-500/40'
                    }`}
                  />
                </div>
                <div className="flex flex-col gap-1.5">
                  <label className={`text-xs font-medium tracking-wide ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>Company <span className={isDark ? 'text-cyan-400' : 'text-blue-500'}>*</span></label>
                  <input
                    type="text"
                    name="company"
                    required
                    placeholder="Acme Legal Group"
                    className={`w-full px-4 py-2.5 rounded-md text-sm placeholder-gray-400 focus:outline-none transition-colors ${
                      isDark
                        ? 'bg-white/5 border border-white/8 text-white focus:border-cyan-400/40'
                        : 'bg-gray-50 border border-gray-200 text-[#1d1d1f] focus:border-blue-500/40'
                    }`}
                  />
                </div>
              </div>

              {/* Email */}
              <div className="flex flex-col gap-1.5">
                <label className={`text-xs font-medium tracking-wide ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>Work Email <span className={isDark ? 'text-cyan-400' : 'text-blue-500'}>*</span></label>
                <input
                  type="email"
                  name="email"
                  required
                  placeholder="jane@acmelegal.com"
                  className={`w-full px-4 py-2.5 rounded-md text-sm placeholder-gray-400 focus:outline-none transition-colors ${
                    isDark
                      ? 'bg-white/5 border border-white/8 text-white focus:border-cyan-400/40'
                      : 'bg-gray-50 border border-gray-200 text-[#1d1d1f] focus:border-blue-500/40'
                  }`}
                />
              </div>

              {/* Industry */}
              <div className="flex flex-col gap-1.5">
                <label className={`text-xs font-medium tracking-wide ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>Industry</label>
                <select
                  name="industry"
                  className={`w-full px-4 py-2.5 rounded-md text-sm focus:outline-none transition-colors cursor-pointer appearance-none ${
                    isDark
                      ? 'bg-[#161B22] border border-white/8 text-gray-300 focus:border-cyan-400/40'
                      : 'bg-gray-50 border border-gray-200 text-gray-700 focus:border-blue-500/40'
                  }`}
                >
                  <option value="">Select your industry</option>
                  {industries.map((ind) => (
                    <option key={ind} value={ind}>{ind}</option>
                  ))}
                </select>
              </div>

              {/* Message */}
              <div className="flex flex-col gap-1.5">
                <div className="flex items-center justify-between">
                  <label className={`text-xs font-medium tracking-wide ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>Brief Overview</label>
                  <span className={`text-xs ${charCount > 480 ? (isDark ? 'text-amber-400' : 'text-amber-500') : (isDark ? 'text-gray-600' : 'text-gray-400')}`}>
                    {charCount}/500
                  </span>
                </div>
                <textarea
                  name="message"
                  rows={3}
                  maxLength={500}
                  placeholder="Describe your current operational challenges or what you'd like to improve..."
                  onChange={(e) => setCharCount(e.target.value.length)}
                  className={`w-full px-4 py-2.5 rounded-md text-sm placeholder-gray-400 focus:outline-none transition-colors resize-none ${
                    isDark
                      ? 'bg-white/5 border border-white/8 text-white focus:border-cyan-400/40'
                      : 'bg-gray-50 border border-gray-200 text-[#1d1d1f] focus:border-blue-500/40'
                  }`}
                />
              </div>

              {/* Error */}
              {status === 'error' && (
                <p className={`text-xs border rounded-md px-4 py-2.5 ${
                  isDark ? 'text-red-400 bg-red-400/8 border-red-400/15' : 'text-red-500 bg-red-500/8 border-red-500/15'
                }`}>
                  Something went wrong. Please try again or contact us directly.
                </p>
              )}

              {/* Submit */}
              <button
                type="submit"
                disabled={status === 'submitting'}
                className={`w-full mt-1 py-3 rounded-md text-sm font-semibold disabled:opacity-50 disabled:cursor-not-allowed transition-all cursor-pointer whitespace-nowrap ${
                  isDark
                    ? 'bg-white text-[#0E1116] hover:bg-gray-100'
                    : 'bg-[#0E1116] text-white hover:bg-gray-800'
                }`}
              >
                {status === 'submitting' ? 'Submitting...' : 'Submit Audit Request'}
              </button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}